/**
 * NotificationBell — the app-wide bell, fixed in the top inline-end corner.
 *
 * Placement: mounted once in Router.tsx (inside the router — it navigates, and
 * outside that context useNavigate throws and the app renders blank), so it is
 * present on every authenticated page EXCEPT a conversation. A chat is a
 * reading surface that owns this corner and asks for the user's attention; a
 * badge floating over someone's message is a distraction there. Pages that do
 * show it reserve room through `--bell-corner-inline` (see semantic.css), the
 * same clamp mechanism the support button uses.
 *
 * The list is a HISTORY, newest first, not just what is still unanswered: an
 * invitation you accepted stays readable instead of vanishing the moment you
 * click. It loads a page at a time and fetches more as you scroll back, so a
 * user with hundreds of invitations doesn't pay for all of them on open.
 *
 * A card opens in place. Clicking a row expands it right there in the list —
 * no second surface, no navigation — which is why the row and the expanded
 * card are the same component in two states rather than two components.
 *
 * Polling, not sockets: an invitation is not time-critical, and one cheap
 * count a minute is the right cost. The count refreshes immediately after any
 * answer so the badge never lags what the user just did.
 *
 * Two kinds today (item 63): project invitations, and "your report is ready"
 * for an Insights report's owner. The bell is there while the organization's
 * bell switch (`notifications.bell`, default on) is on AND something can
 * notify this person - Projects, or the Reports tab with an Insights role. A
 * Projects-only organization sees exactly the bell it had.
 */

import { isPopupLandingPath } from '@/routing/popupLandings';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useFeature } from '@/hooks/useFeature';
import { useTenantPath } from '@/routing';
import { useTranslator } from '../hooks/useTranslator';
import projectService from '../services/projectService';
import type { AppNotification } from '../types';
import { InvitationCard } from './InvitationCard';
import { ReportReadyCard } from './ReportReadyCard';
import { SkeletonRows, ErrorBlock } from './StateBlocks';
import './NotificationBell.css';

const POLL_INTERVAL_MS = 60000;
/** Small enough that the first paint is instant, big enough to fill the panel. */
const PAGE_SIZE = 8;

export function NotificationBell() {
  const { t, tRef } = useTranslator();
  const { isAuthenticated, user } = useAuth();
  const projectsEnabled = useFeature('modules.projects');
  const bellOn = useFeature('notifications.bell');
  const insightsOn = useFeature('modules.insights');
  const reportsOn = useFeature('insights.report');
  const reportsEnabled = insightsOn && reportsOn
    && ['super_admin', 'group_admin'].includes(user?.user_role || '');
  // The server decides the same (backend/modules/notifications/services/bell.py).
  const bellAvailable = bellOn && (projectsEnabled || reportsEnabled);
  const navigate = useNavigate();
  const location = useLocation();
  const tenantPath = useTenantPath();

  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Which card is expanded. Only one at a time — this is a list, not a wall. */
  const [openId, setOpenId] = useState<string | null>(null);

  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  /** Guards against a scroll burst firing several identical page requests. */
  const fetchingRef = useRef(false);

  const refreshCount = useCallback(() => {
    projectService
      .unreadCount()
      .then(setCount)
      // A failing badge poll stays silent: it is background chrome, and an
      // error toast every 60s on a flaky network is worse than no badge.
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !bellAvailable) return;
    refreshCount();
    const id = window.setInterval(refreshCount, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [isAuthenticated, bellAvailable, refreshCount]);

  const loadFirstPage = useCallback(() => {
    setLoading(true);
    setError(null);
    setOpenId(null);
    projectService
      .listNotifications(PAGE_SIZE, 0)
      .then(page => {
        setItems(page.notifications);
        setHasMore(page.hasMore);
      })
      .catch(() => setError(
        tRef.current('projects.bell.loadFailed') || t('projects.notifications.loadFailed'),
      ))
      .finally(() => setLoading(false));
    // `t` out of the deps on purpose — see useTranslator.ts.
  }, [tRef]);

  const loadNextPage = useCallback(() => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoadingMore(true);
    // Offset from what we already hold, so a notification arriving mid-scroll
    // can't make us skip a row.
    projectService
      .listNotifications(PAGE_SIZE, items.length)
      .then(page => {
        setItems(prev => {
          const seen = new Set(prev.map(n => n.notification_id));
          return [...prev, ...page.notifications.filter(n => !seen.has(n.notification_id))];
        });
        setHasMore(page.hasMore);
      })
      .catch(() => setHasMore(false))
      .finally(() => { fetchingRef.current = false; setLoadingMore(false); });
  }, [items.length]);

  /**
   * Place the panel under the bell, measured from the DOM so it tracks the
   * button rather than assuming a corner offset.
   */
  const [box, setBox] = useState<{ top: number; left: number } | null>(null);
  const placePanel = useCallback(() => {
    const anchor = anchorRef.current?.getBoundingClientRect();
    if (!anchor) return;
    const width = Math.min(400, window.innerWidth - 24);
    const gap = 10;
    const rtl = document.documentElement.getAttribute('dir') === 'rtl';
    const left = rtl ? anchor.left : anchor.right - width;
    setBox({
      top: anchor.bottom + gap,
      left: Math.min(Math.max(left, 12), Math.max(12, window.innerWidth - width - 12)),
    });
  }, []);

  const handleOpen = () => {
    placePanel();
    setOpen(true);
    loadFirstPage();
    // Opening the bell IS reading it: the badge clears, while each card keeps
    // its own unread rail for this session so you can still tell which were new.
    projectService.markAllRead().then(() => setCount(0)).catch(() => undefined);
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onReflow = () => placePanel();
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onReflow);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onReflow);
    };
  }, [open, placePanel]);

  // Infinite scroll: a sentinel at the end of the list pulls the next page as
  // it comes into view. An observer rather than a scroll handler so it costs
  // nothing while the user is reading.
  useEffect(() => {
    if (!open || !hasMore || loading) return;
    const node = sentinelRef.current;
    const root = panelRef.current?.querySelector('.bell-panel-body');
    if (!node || !root) return;
    const observer = new IntersectionObserver(
      entries => { if (entries.some(e => e.isIntersecting)) loadNextPage(); },
      { root, rootMargin: '120px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [open, hasMore, loading, loadNextPage]);

  const handleRespond = async (memberId: string, accept: boolean) => {
    if (accept) await projectService.acceptInvitation(memberId);
    else await projectService.declineInvitation(memberId);
    refreshCount();
  };

  const handleOpenProject = (projectId: string) => {
    setOpen(false);
    navigate(tenantPath(`/projects/${projectId}`));
  };

  const handleOpenReport = (reportId: string) => {
    setOpen(false);
    navigate(tenantPath(`/insights?tab=reports&report=${encodeURIComponent(reportId)}`));
  };

  // The bell is chrome for signed-in users of the module — never on the login
  // screen, never for a tenant without Projects, and never over a
  // conversation. A chat is a reading surface with its own controls in this
  // corner, and a badge floating over someone's message is a distraction in
  // the one place the product asks for attention.
  const inConversation = /\/(chat|concierge)(\/|\?|$)/.test(location.pathname + location.search);
  // A consent popup's landing page only says how it ended and closes.
  if (!isAuthenticated || !bellAvailable || inConversation || isPopupLandingPath(location.pathname)) return null;

  const badgeLabel = count > 99 ? '99+' : String(count);
  const title = t('projects.bell.title') || 'Notifications';

  return (
    <>
      <div className="bell-corner" ref={anchorRef}>
        <button
          type="button"
          className={`bell-button ${open ? 'is-open' : ''}`}
          onClick={() => (open ? setOpen(false) : handleOpen())}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={count > 0 ? `${title} (${badgeLabel})` : title}
          title={title}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.7 21a2 2 0 0 1-3.4 0" />
          </svg>
          {count > 0 && (
            <span className="bell-badge" aria-live="polite" aria-atomic="true">
              {badgeLabel}
            </span>
          )}
        </button>
      </div>

      {open && createPortal(
        <div
          className="bell-panel"
          ref={panelRef}
          style={box ? { top: box.top, left: box.left, insetInlineStart: 'auto' } : undefined}
          role="dialog"
          aria-label={title}
        >
          <header className="bell-panel-header">
            <h2 className="bell-panel-title">{title}</h2>
            <button
              type="button"
              className="pj-icon-btn"
              onClick={() => setOpen(false)}
              aria-label={t('common.close') || 'Close'}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </header>

          <div className="bell-panel-body">
            {loading ? (
              <SkeletonRows rows={3} />
            ) : error ? (
              <ErrorBlock message={error} onRetry={loadFirstPage} />
            ) : items.length === 0 ? (
              <div className="bell-empty">
                <span className="bell-empty-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.7 21a2 2 0 0 1-3.4 0" />
                  </svg>
                </span>
                <p className="bell-empty-title">{t('projects.bell.emptyTitle') || 'Nothing new'}</p>
                <p className="bell-empty-body">
                  {reportsEnabled
                    ? t(projectsEnabled ? 'insights.reports.bell.emptyBodyBoth' : 'insights.reports.bell.emptyBody')
                    : (t('projects.bell.emptyBody') || 'Project invitations will appear here.')}
                </p>
              </div>
            ) : (
              <div className="bell-list">
                {items.map(notification => (notification.type === 'insight_report_ready' ? (
                  <ReportReadyCard
                    key={notification.notification_id}
                    notification={notification}
                    onOpen={handleOpenReport}
                  />
                ) : (
                  <InvitationCard
                    key={notification.notification_id}
                    notification={notification}
                    expanded={openId === notification.notification_id}
                    onToggle={() => setOpenId(
                      openId === notification.notification_id
                        ? null
                        : notification.notification_id,
                    )}
                    onRespond={handleRespond}
                    onOpen={handleOpenProject}
                  />
                )))}

                {/* Pulls the next page into view as you scroll back. */}
                {hasMore && (
                  <div className="bell-sentinel" ref={sentinelRef}>
                    {loadingMore && (
                      <span className="bell-more">
                        {t('common.loading') || 'Loading…'}
                      </span>
                    )}
                  </div>
                )}
                {!hasMore && items.length > PAGE_SIZE && (
                  <p className="bell-end">
                    {t('projects.bell.end') || "That's everything."}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

export default NotificationBell;
